import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {CurrencyPipe, isPlatformBrowser, NgForOf, NgIf} from '@angular/common';
import {Room} from '../../../models/room';
import {RoomService} from '../../../services/room-service';
import {finalize, forkJoin, Subject, takeUntil} from 'rxjs';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {Region} from '../../../models/region';
import {RegionService} from '../../../services/region-service';
import {MapMark, MapService} from '../../../services/map.service';
import {User} from '../../../models/user';
import {AuthService} from '../../../services/auth.service';
import {RouteService} from '../../../services/route.service';
import {environment} from '../../../environments/environment';
import {RoomBoxComponent} from '../room-box/room-box.component';
import {FormsModule} from '@angular/forms';
import moment from "moment";
import {ScheduleService} from '../../../services/schedule.service';
import {Availability, Schedule} from '../../../models/schedule';
import {MomentPipe} from '../../pipes/moment.pipe';
import {PriceService} from '../../../services/price.service';
import {Product} from '../../../models/product';
import {ProductService} from '../../../services/product.service';
import {BookingPrice} from '../../../models/price';
import {MoneyPipe} from '../../pipes/money.pipe';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BookingJoinModalComponent} from './booking-join-modal/booking-join-modal.component';
import {Place} from '../../../models/place';
import {PlaceService} from '../../../services/place.service';
import {PlaceDetailHoursComponent} from '../../discover/place-detail/place-detail-hours/place-detail-hours.component';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import {BookingConfirmModalComponent} from './booking-confirm-modal/booking-confirm-modal.component';
import {SeoService} from '../../../services/seo.service';

@Component({
  selector: 'app-room-detail',
  imports: [
    NgIf,
    RouterLink,
    RoomBoxComponent,
    NgForOf,
    FormsModule,
    MomentPipe,
    MoneyPipe,
    PlaceDetailHoursComponent
  ],
  providers: [CurrencyPipe],
  templateUrl: './room-detail.component.html',
  styleUrl: './room-detail.component.scss'
})
export class RoomDetailComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  GOOGLE_MAP_API_KEY = environment.GOOGLE_MAP_API_KEY

  loading = false

  // Region
  regionSlug: string | null = null
  region: Region | null = null

  // Neighborhood
  neighborhoodSlug: string | null = null

  // Place
  placeSlug: string | null = null
  place: Place | null = null

  // Room
  roomSlug: string | null = null
  room: Room | null = null

  // Rooms
  isRoomsLoading = false
  isRoomsLoaded = false
  rooms: Room[] = []
  otherRooms: Room[] = []

  // User
  currentUser: User | null = null;

  // Context navigation
  previousPath: string = "/conference-room"

  // Product
  isProductLoading = false
  isProductLoaded = false
  product: Product | null = null;

  // Reserve options
  reserve: string | null = null
  reserveDate: string | null = null
  reserveTime: string | null = null
  reserveDuration: number | null = null

  reserveDateOptions: { label: string, value: string }[] = [];
  reserveTimeOptions: { label: string, value: string }[] = [];
  reserveDurationOptions: { label: string, value: number }[] = [];

  // Schedule
  isScheduleLoading = false;
  isScheduleLoaded = false;
  schedule: Schedule | null = null;

  // Availability
  selectedAvailability: Availability | null = null;

  // Booking
  isBookingPriceLoading = false;
  bookingPrice: BookingPrice | null = null;

  // Gallery
  images: any[] = []
  gallery: any | null = null

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private route: ActivatedRoute,
              private modal: NgbModal,
              private routeService: RouteService,
              private authService: AuthService,
              private regionService: RegionService,
              private roomService: RoomService,
              private placeService: PlaceService,
              private mapService: MapService,
              private productService: ProductService,
              private scheduleService: ScheduleService,
              private priceService: PriceService,
              private seoService: SeoService) {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser;
    });
  }

  ngOnInit() {
    // Context navigation based on /discover/region/neighborhood/place
    this.routeService.previousPath$.pipe(takeUntil(this.onDestroy$)).subscribe(previousPath => {
      if (previousPath && previousPath.startsWith("/conference-room")) {

        // Preventing same place's room jumping on setting previous path since we want to go back to search
        // ["", "conference-room", "region", "neighborhood", "place", "room"]
        if (previousPath.split("/").length > 5) {
          return;
        }

        // Remove query parameters because it's already handled by router link (queryParamsHandling)
        if (previousPath.split("?").length > 1) {
          previousPath = previousPath.split("?")[0]
        }

        this.previousPath = previousPath;
      }
    });

    this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.regionSlug = params['regionSlug']
      this.neighborhoodSlug = params['neighborhoodSlug'];
      this.placeSlug = params['placeSlug'];
      this.roomSlug = params['roomSlug'];

      this.refresh();

      // Angular doesn't know how to scroll up on a sub view component
      if (isPlatformBrowser(this.platformId)) {
        document.getElementById("room-detail-component")?.scrollIntoView({behavior: 'smooth'});
      }
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refresh() {
    if (!this.regionSlug) return;
    if (!this.placeSlug) return;
    if (!this.roomSlug) return;

    this.loading = true;

    forkJoin([
      this.roomService.getBySlug(this.roomSlug),
      this.placeService.getBySlug(this.placeSlug),
      this.regionService.getBySlug(this.regionSlug)]).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.loading = false))
      .subscribe((results) => {
        this.room = results[0];
        this.place = results[1]
        this.region = results[2];

        this.refreshSeo();
        this.refreshGallery()
        this.refreshRooms();
        this.refreshProduct();
        this.refreshReserveOptions();
      });
  }

  refreshGallery() {
    if (!this.room) return;

    this.images = this.room.largeImages

    // Load photoswipe
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.gallery = new PhotoSwipeLightbox({
          gallery: '#gallery',
          children: 'a',
          pswpModule: () => import('photoswipe') // Dynamically load
        });
        this.gallery.init();
      }, 0)
    }
  }

  showGallery(index: number) {
    if (this.gallery) this.gallery.loadAndOpen(index);
  }

  refreshRooms() {
    if (!this.room) {
      return;
    }

    this.isRoomsLoading = true
    this.roomService.findByPlaceId(this.room.place!._id!).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRoomsLoading = false
        this.isRoomsLoaded = true
      }))
      .subscribe(rooms => {
        this.rooms = rooms

        this.otherRooms = rooms
          .filter(room => room._id != this.room!._id)
          .sort((a, b) => a.maxSeats! - b.maxSeats!)

        this.refreshMap();
      })
  }

  refreshProduct() {
    if (!this.room) {
      return;
    }

    const productSlug = (this.room.currency === 'usd') ? 'extra-conference' : 'extra-conference-' + this.room.currency;

    this.isProductLoading = true;
    this.productService.getBySlug(productSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isProductLoading = false
        this.isProductLoaded = true
      }))
      .subscribe(product => {
        this.product = product
      });
  }

  refreshMap() {
    if (!this.room) {
      return;
    }

    const mapMark: MapMark = {
      latitude: this.room.place!.location!.latitude!,
      longitude: this.room.place!.location!.longitude!,
      place: this.room.place!,
      isOpen: this.room.place!.isOpen!,
      rooms: this.rooms
    };

    this.mapService.setMapState(this.room.place!.location!.latitude!, this.room.place!.location!.longitude!, 16, [mapMark]);
  }

  refreshSeo() {
    if (!this.room) return;
    if (!this.place) return;
    if (!this.region) return;

    let title = `Croissant | ${this.room.name} Conference Room at ${this.room.place.name} | ${this.room.place.location!.neighborhood}, ${this.region.name}`
    let description = this.place.description

    this.seoService.setTitle(title)
    this.seoService.setDescription(description)
    if (this.room.smallImages.length > 0) this.seoService.setImage(this.room.smallImages[0].filePath)
  }

  refreshReserveOptions() {

    // Reserve date options [2025-01-01 -> 2025-03-01]
    let currentDay = moment();
    for (let i = 0; i < 60; i++) {
      this.reserveDateOptions.push({
        value: currentDay.format('yyyy-MM-DD'),
        label: currentDay.format('dddd, MMM Do, YYYY')
      });
      currentDay.add(1, "day")
    }

    // Reserve time options [07:00 -> 20:00]
    let currentTime = moment().hour(7).minute(0).second(0);
    for (let i = 0; i < 27; i++) {
      this.reserveTimeOptions.push({
        value: currentTime.format('hh:mm:ss'),
        label: currentTime.format('h:mm a')
      });

      currentTime = currentTime.add(30, 'minutes');
    }

    // Reserve duration options [0.5 -> 8.0]
    for (let i = 0.5; i <= 8; i += 0.5) {
      this.reserveDurationOptions.push({
        value: i,
        label: i === 0.5 ? '30 mins' : Math.floor(i) === i ? i + ' hour' + (i !== 1 ? 's' : '') : Math.floor(i) + ' hours & 30 mins',
      });
    }

    // Query defaults
    const queryParamsReserve = this.route.snapshot.queryParams['reserve']
    const queryParamsReserveDuration = this.route.snapshot.queryParams['reserveDuration']

    if (queryParamsReserve) this.reserveDate = moment(queryParamsReserve).format('yyyy-MM-DD')
    if (queryParamsReserve) this.reserveTime = moment(queryParamsReserve).format('hh:mm:ss')
    if (queryParamsReserveDuration) this.reserveDuration = queryParamsReserveDuration

    let skipDays = 1 // Always tomorrow
    if (moment().weekday() == 5) skipDays += 2 // Friday   -> Monday
    if (moment().weekday() == 6) skipDays += 1 // Saturday -> Monday
    if (moment().weekday() == 7) skipDays += 0 // Sunday   -> Monday

    let skipTimeSlot = 4; // 9:00 am
    // TODO - Filter time slot based on when the space opens

    let skipDuration = 1 // 1 hour
    // TODO - Filter duration based on minimum time slot

    // Defaults
    if (!this.reserveDate) this.reserveDate = this.reserveDateOptions[skipDays].value      // Monday
    if (!this.reserveTime) this.reserveTime = this.reserveTimeOptions[skipTimeSlot].value  // 9:00 am
    if (!this.reserveDuration) this.reserveDuration = this.reserveDurationOptions[skipDuration].value // 1 hour

    this.loadSchedule()
  }

  loadSchedule() {
    if (!this.room) return
    if (!this.reserveDate) return
    if (!this.reserveTime) return
    if (!this.reserveDuration) return

    this.reserve = `${this.reserveDate}T${this.reserveTime}`

    const roomId = this.room._id
    const date = this.reserve
    const duration = this.reserveDuration * 60 * 60 * 1000; // Hours to milliseconds

    this.isScheduleLoading = true
    this.scheduleService.getByRoomId(roomId, date, duration).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isScheduleLoading = false
        this.isScheduleLoaded = true
      }))
      .subscribe(schedule => {
        this.schedule = schedule

        // Default to select the first available timeslot
        if (this.schedule.availabilities.length === 1) {
          this.selectedAvailability = this.schedule.availabilities[0]
          this.loadBookingPrices()

        } else {

          // Resetting the selected availability
          this.selectedAvailability = null
          this.bookingPrice = null
        }
      })
  }

  loadBookingPrices() {
    if (!this.room) return
    if (!this.product) return
    if (!this.selectedAvailability) return

    const productIds: string[] = [this.product._id]
    const referralIds: string[] = []
    const promotionIds: string[] = []
    const roomId: string = this.room._id!
    const localBegin = this.selectedAvailability.localBegin.toString()
    const localEnd = this.selectedAvailability.localEnd.toString()

    this.isBookingPriceLoading = true
    this.priceService.findBookingPrices(productIds, referralIds, promotionIds, roomId, localBegin, localEnd).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isBookingPriceLoading = false))
      .subscribe(bookingPrice => {
        this.bookingPrice = bookingPrice
      })
  }

  bookRoom() {
    if (!this.currentUser) return this.bookRoomForNewCustomer()
    if (this.currentUser) return this.bookRoomForExistingCustomer()
  }

  bookRoomForNewCustomer() {
    const bookingJoinModal = this.modal.open(BookingJoinModalComponent, {centered: true, backdrop: "static"})
    bookingJoinModal.componentInstance.regionSlug = this.regionSlug
    bookingJoinModal.componentInstance.room = this.room
    bookingJoinModal.componentInstance.product = this.product
    bookingJoinModal.componentInstance.availability = this.selectedAvailability
    bookingJoinModal.componentInstance.bookingPrice = this.bookingPrice
  }

  bookRoomForExistingCustomer() {
    const bookingConfirmModal = this.modal.open(BookingConfirmModalComponent, {centered: true, backdrop: "static"})
    bookingConfirmModal.componentInstance.currentUser = this.currentUser
    bookingConfirmModal.componentInstance.regionSlug = this.regionSlug
    bookingConfirmModal.componentInstance.room = this.room
    bookingConfirmModal.componentInstance.product = this.product
    bookingConfirmModal.componentInstance.availability = this.selectedAvailability
    bookingConfirmModal.componentInstance.bookingPrice = this.bookingPrice
  }

}
